import { Flex, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useUserState } from '~/atoms/UserState'
import { DateTimeRangeDropdown } from '~/components/shared/DateTimeRangeDropdown'
import { FilterButton } from '~/components/shared/FilterButton'
import { useGetCustomStatuses } from '~/queries/customStatusQueries'
import { DateRange, FilterSection, SelectedFilterData } from '~/types/Shared'
import { UserAgentEventLogTypeDescription } from '~/types/UserAgentEventLog'

export interface EventLogFiltersProps {
  onFilterChangesApplied: (filters: {
    eventTypes: string[]
    details: string[]
  }) => void
  onDateTimeRangeChange: (dateTimeRange: DateRange) => void
}

const EventLogFilters = ({
  onFilterChangesApplied,
  onDateTimeRangeChange,
}: EventLogFiltersProps) => {
  const { pbxId } = useUserState()
  const { data: customStatuses } = useGetCustomStatuses(pbxId)
  const [activeFilters, setActiveFilters] = useState<SelectedFilterData[]>([])
  const [dateTimeRange, setDateTimeRange] = useState<DateRange>(
    DateRange.last30Days(),
  )

  const initialFilterData = useMemo(
    () => [
      {
        title: 'Event Type',
        rows: [
          {
            items: Object.entries(UserAgentEventLogTypeDescription).map(
              ([key, value]) => ({
                title: value,
                data: key,
                isSelected: false,
              }),
            ),
          },
        ],
      },
      {
        title: 'Status',
        rows: [
          {
            items:
              customStatuses?.map((status) => ({
                title: status.name,
                data: status.id,
                isSelected: false,
              })) || [],
          },
        ],
      },
    ],
    [customStatuses],
  )

  // Update filterData whenever initialFilterData changes
  useEffect(() => {
    setFilterData(initialFilterData)
  }, [initialFilterData])

  const [filterData, setFilterData] =
    useState<FilterSection[]>(initialFilterData)

  const handleDateTimeRangeChange = (dateTimeRange: DateRange) => {
    setDateTimeRange(dateTimeRange)
    onDateTimeRangeChange(dateTimeRange)
  }

  const handleFilterChangesApplied = (updatedFilters: FilterSection[]) => {
    // Extract selected filters
    const newActiveFilters: SelectedFilterData[] = []

    updatedFilters.forEach((section) => {
      const sectionTitle = section.title
      section.rows.forEach((row) => {
        const rowTitle = row.title
        row.items.forEach((item) => {
          if (item.isSelected) {
            newActiveFilters.push({
              sectionTitle,
              rowTitle,
              itemTitle: item.title,
              item,
            })
          }
        })
      })
    })

    setActiveFilters(newActiveFilters)
    setFilterData(updatedFilters)

    //TODO multi select not supported by the api
    const eventTypeFilters = newActiveFilters
      .filter((f) => f.sectionTitle === 'Event Type')
      .map((f) => f.item.data as string)

    const statusFilters = newActiveFilters
      .filter((f) => f.sectionTitle === 'Status')
      .map(
        (f) =>
          customStatuses?.find((status) => status.id === f.item.data)?.name ||
          '',
      )

    // Call your query/filter function with the new filters
    onFilterChangesApplied({
      eventTypes: eventTypeFilters,
      details: statusFilters,
    })
  }

  return (
    <Flex
      width={'100%'}
      height={'4.5rem'}
      padding={'0rem 1.25rem'}
      justifyContent={'space-between'}
      alignItems={'center'}
      alignSelf={'stretch'}
      borderBottom={'2px solid var(--Gray-Gray400, #E5E5EE)'}
      background={'var(--Gray-Gray200, #F6F6FA);'}
    >
      <Text
        fontSize={'1.25rem'}
        fontWeight={'800'}
        lineHeight={'1.75rem'}
        color={'var(--Gray-Gray1300, #1D1D30);'}
      >
        Event Log
      </Text>
      <Flex gap={'0.75rem'}>
        <DateTimeRangeDropdown
          onValueChange={handleDateTimeRangeChange}
          useQuickActionButton={false}
          dateTimeRange={dateTimeRange}
        />
        <div style={{ display: 'none' }}>
          <FilterButton
            filterData={filterData}
            title="Filter"
            activeFiltersCount={activeFilters.length}
            onFilterChangesApplied={handleFilterChangesApplied}
            useQuickActionButton={false}
          />
        </div>
      </Flex>
    </Flex>
  )
}

export default EventLogFilters
